import type { SecurityStatus } from "@unbox/api";
import { CheckCircle, Key01, Lock01, Passcode, RefreshCw01, ShieldTick } from "@untitledui/icons";
import { useState } from "react";
import { useRegenerateBackupCodes, useSecurityStatus, useStartTwoFactorSetup } from "@/api/security";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { endSessions } from "@/hooks/use-sign-out";
import { success } from "@/providers/ToastProvider";
import { cx } from "@/utils/cx";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";
import { BackupCodesPanel } from "./security/backup-codes-panel";
import { ChangePasswordDialog } from "./security/change-password-dialog";
import { ConfirmPasswordPanel } from "./security/confirm-password-panel";
import { DisableTwoFactorDialog } from "./security/disable-two-factor-dialog";
import { SettingsIcon } from "./security/panel-heading";
import { ScanPanel } from "./security/scan-panel";

type Panel =
  | { kind: "overview" }
  | { kind: "enable-password" }
  | { kind: "enable-scan"; totpURI: string; backupCodes: string[] }
  | { kind: "enable-codes"; backupCodes: string[] }
  | { kind: "regenerate-password" }
  | { kind: "regenerate-codes"; backupCodes: string[] };

const OVERVIEW: Panel = { kind: "overview" };

const formatDay = (date: string) =>
  new Date(date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const PasswordCard = ({ changedAt, onChange }: { changedAt: string | null; onChange: () => void }) => (
  <div className="min-w-0 flex-1 rounded-2xl border border-gray-200 bg-white">
    <section className="flex flex-wrap items-center gap-x-6 gap-y-3 p-6">
      <SettingsIcon>
        <Passcode className="size-5" />
      </SettingsIcon>
      <div className="min-w-[260px] flex-1">
        <h2 className="text-base font-medium text-gray-900">Password</h2>
        {changedAt && (
          <p className="mt-1 text-sm text-gray-600">
            Last changed <span className="font-semibold">{formatDay(changedAt)}</span>.
          </p>
        )}
      </div>
      <Button color="secondary" onClick={onChange}>
        Change password
      </Button>
    </section>
  </div>
);

const SsoManagedCard = () => (
  <div className="min-w-0 flex-1 rounded-2xl border border-gray-200 bg-white">
    <section className="flex flex-wrap items-center gap-x-6 gap-y-3 p-6">
      <SettingsIcon>
        <Key01 className="size-5" />
      </SettingsIcon>
      <div className="min-w-[260px] flex-1">
        <h2 className="text-base font-medium text-gray-900">Single sign-on</h2>
        <p className="mt-1 max-w-[560px] text-sm text-gray-600">
          You sign in through your company's single sign-on. Your password and two-step verification are managed there.
        </p>
      </div>
    </section>
  </div>
);

const TwoFactorOff = ({ onEnable }: { onEnable: () => void }) => (
  <section className="flex flex-wrap items-start gap-x-6 gap-y-4 p-6">
    <SettingsIcon>
      <ShieldTick className="size-5" />
    </SettingsIcon>
    <div className="min-w-[260px] flex-1">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-medium text-gray-900">Two-factor authentication</h2>
        <Badge size="sm" color="gray">
          Off
        </Badge>
      </div>
      <p className="mt-1 max-w-[560px] text-sm text-gray-600">
        Add a second step at sign-in: a code from an authenticator app on your phone, so a stolen password isn't enough
        to get into your account.
      </p>
    </div>
    <Button onClick={onEnable}>Enable two-factor authentication</Button>
  </section>
);

const backupCodesSummary = (remaining: number, total: number) =>
  remaining === 0
    ? `0 of ${total} remaining. Regenerate to get a new set.`
    : `${remaining} of ${total} remaining. Regenerating replaces every unused code.`;

const TwoFactorOn = ({
  status,
  onRegenerate,
  onDisable,
}: {
  status: SecurityStatus;
  onRegenerate: () => void;
  onDisable: () => void;
}) => (
  <>
    <section className="flex flex-wrap items-start gap-x-6 gap-y-4 p-6">
      <SettingsIcon>
        <ShieldTick className="size-5" />
      </SettingsIcon>
      <div className="min-w-[260px] flex-1">
        <h2 className="text-base font-medium text-gray-900">Two-factor authentication</h2>
        {status.twoFactorEnabledAt && (
          <p className="mt-1 text-sm text-gray-600">
            Enabled on <span className="font-semibold">{formatDay(status.twoFactorEnabledAt)}</span>.
          </p>
        )}
      </div>
      <span className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-success-50 px-3.5 py-2 text-sm leading-5 font-semibold text-success-700">
        <CheckCircle className="size-[18px]" aria-hidden />
        Enabled
      </span>
    </section>
    <section className="flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-gray-200 p-6">
      <SettingsIcon>
        <Key01 className="size-5" />
      </SettingsIcon>
      <div className="min-w-[260px] flex-1">
        <h3 className="text-base font-medium text-gray-900">Backup codes</h3>
        <p className="mt-0.5 text-sm text-gray-600">
          {backupCodesSummary(status.backupCodesRemaining ?? 0, status.backupCodesTotal)}
        </p>
      </div>
      <Button color="secondary" iconLeading={RefreshCw01} onClick={onRegenerate}>
        Regenerate codes
      </Button>
    </section>
    <section className="flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-gray-200 p-6">
      <div className="min-w-[260px] flex-1">
        <h3 className="text-base font-medium text-gray-900">Disable two-factor authentication</h3>
        <p className="mt-0.5 text-sm text-gray-600">Your account goes back to password-only sign-in.</p>
      </div>
      <Button color="primary-destructive" onClick={onDisable}>
        Disable two-factor authentication
      </Button>
    </section>
  </>
);

export const SecurityPage = () => {
  const { data: status } = useSecurityStatus();
  const startSetup = useStartTwoFactorSetup();
  const regenerate = useRegenerateBackupCodes();
  const [panel, setPanel] = useState<Panel>(OVERVIEW);
  const [changingPassword, setChangingPassword] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const isOverview = panel.kind === "overview";

  const confirmSetup = async (password: string) => {
    const setup = await startSetup.mutateAsync(password);
    if (setup.method !== "totp") throw new Error("Two-factor setup did not return an authenticator key");
    setPanel({ kind: "enable-scan", totpURI: setup.totpURI, backupCodes: setup.backupCodes });
  };

  const confirmRegenerate = async (password: string) => {
    const { backupCodes } = await regenerate.mutateAsync(password);
    setPanel({ kind: "regenerate-codes", backupCodes });
  };

  const finishSetup = () => {
    setPanel(OVERVIEW);
    success("Two-factor authentication is on", {
      description: "You'll be asked for a code the next time you sign in.",
    });
  };

  const finishRegenerate = () => {
    setPanel(OVERVIEW);
    success("Backup codes regenerated", { description: "Your old codes no longer work." });
  };

  const forgotPassword = async () => {
    setChangingPassword(false);
    await endSessions();
    window.location.assign("/auth/forgot-password");
  };

  return (
    <div className="flex h-screen w-full flex-col items-center overflow-y-auto bg-unbox-cream-25">
      <div className={`${LAYOUT_CONSTRAINTS.CONTAINER_WIDTH} flex flex-col gap-8 px-6 pt-16 pb-8 lg:px-16`}>
        <div className="flex flex-col gap-1">
          <h1 className="mb-1 flex flex-row items-center gap-2 text-xl font-normal text-secondary">
            <Lock01 className="h-5 w-5" strokeWidth={1.8} />
            Security
          </h1>
          <span className="text-sm text-quaternary">Your password and two-factor authentication</span>
        </div>

        {status?.ssoManaged && <SsoManagedCard />}

        {status && !status.ssoManaged && (
          <>
            {isOverview && (
              <PasswordCard changedAt={status.passwordChangedAt} onChange={() => setChangingPassword(true)} />
            )}
            <div
              className={cx(
                "rounded-2xl",
                isOverview
                  ? "min-w-0 flex-1 border border-gray-200 bg-white"
                  : "w-[610px] max-w-full bg-unbox-cream-50",
              )}
            >
              {isOverview && !status.twoFactorEnabled && (
                <TwoFactorOff onEnable={() => setPanel({ kind: "enable-password" })} />
              )}
              {isOverview && status.twoFactorEnabled && (
                <TwoFactorOn
                  status={status}
                  onRegenerate={() => setPanel({ kind: "regenerate-password" })}
                  onDisable={() => setDisabling(true)}
                />
              )}
              {panel.kind === "enable-password" && (
                <ConfirmPasswordPanel
                  eyebrow="Step 1 of 3"
                  title="Confirm your password"
                  description="Prove it's you before changing how sign-in works."
                  onCancel={() => setPanel(OVERVIEW)}
                  onConfirm={confirmSetup}
                />
              )}
              {panel.kind === "enable-scan" && (
                <ScanPanel
                  totpURI={panel.totpURI}
                  onCancel={() => setPanel(OVERVIEW)}
                  onVerified={() => setPanel({ kind: "enable-codes", backupCodes: panel.backupCodes })}
                />
              )}
              {panel.kind === "enable-codes" && (
                <BackupCodesPanel
                  eyebrow="Step 3 of 3"
                  title="Two-factor authentication is on"
                  description="Save these backup codes somewhere safe — this is the only time they're shown. Each code signs you in once if you lose your phone."
                  codes={panel.backupCodes}
                  onDone={finishSetup}
                />
              )}
              {panel.kind === "regenerate-password" && (
                <ConfirmPasswordPanel
                  title="Confirm your password"
                  description="Prove it's you before regenerating backup codes."
                  onCancel={() => setPanel(OVERVIEW)}
                  onConfirm={confirmRegenerate}
                />
              )}
              {panel.kind === "regenerate-codes" && (
                <BackupCodesPanel
                  title="Your new backup codes"
                  description="Save these somewhere safe — this is the only time they're shown."
                  replaceWarning="These replace your previous set. Any codes you saved before no longer work."
                  codes={panel.backupCodes}
                  onDone={finishRegenerate}
                />
              )}
            </div>
          </>
        )}
      </div>

      <ChangePasswordDialog
        open={changingPassword}
        onOpenChange={setChangingPassword}
        onForgotPassword={forgotPassword}
      />
      <DisableTwoFactorDialog open={disabling} onOpenChange={setDisabling} />
    </div>
  );
};
